import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { useToast } from '../../ui/use-toast';
import { 
  Users, 
  UserPlus, 
  Shield,
  Eye,
  Crown,
  Monitor,
  HardHat,
  Tv,
  Search,
  Filter,
  Download,
  Upload,
  Edit,
  Trash2,
  Key,
  Mail,
  Phone,
  Building2,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  MoreVertical,
  FileSpreadsheet,
  Copy,
  Settings
} from 'lucide-react';
import { User, Team, Project, Location } from '../../../types';
import { apiService } from '../../../services/api';
import { PasswordRegeneration } from '../PasswordRegeneration';
import { OrganizationalStructureExporter } from '../OrganizationalStructureExporter';
import { WorkSettingsDialog } from '../WorkSettingsDialog';
import * as XLSX from 'xlsx';

interface UserManagementTabProps {
  allUsers: User[];
  teams: Team[];
  projects: Project[];
  locations: Location[];
  onUpdateUser: (user: User) => void;
  onRemoveUser: (userId: number) => void;
  onRefresh: () => void;
  isLoading: boolean;
}

const ROLE_ICONS = {
  admin: Shield,
  supervisor: Crown,
  monitor: Monitor,
  worker: HardHat,
  table: Tv
};

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-800',
  supervisor: 'bg-purple-100 text-purple-800',
  monitor: 'bg-blue-100 text-blue-800',
  worker: 'bg-green-100 text-green-800',
  table: 'bg-gray-100 text-gray-800'
};

const ROLE_NAMES = {
  admin: 'Administrator',
  supervisor: 'Supervisor',
  monitor: 'Monitor',
  worker: 'Muncitor',
  table: 'Masă'
};

export const UserManagementTab: React.FC<UserManagementTabProps> = ({
  allUsers,
  teams,
  projects,
  locations,
  onUpdateUser,
  onRemoveUser,
  onRefresh,
  isLoading
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState<{ userName: string; newPassword: string } | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [workSettingsDialog, setWorkSettingsDialog] = useState<{
    open: boolean;
    userId: number;
    userName: string;
  }>({ open: false, userId: 0, userName: '' });
  const { toast } = useToast();

  // New user form state
  const [newUser, setNewUser] = useState({
    name: '',
    role: 'worker' as User['role'],
    email: '',
    phone: '',
    teamId: '',
    workerType: 'Angajat',
    assignedLocationId: '',
    assignedProjectId: ''
  });

  // Filter users
  const filteredUsers = useMemo(() => {
    let filtered = allUsers;
    
    if (searchQuery) {
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.id.toString().includes(searchQuery)
      );
    }
    
    if (selectedRole !== 'all') {
      filtered = filtered.filter(user => user.role === selectedRole);
    }
    
    return filtered;
  }, [allUsers, searchQuery, selectedRole]);

  // Group users by role
  const usersByRole = useMemo(() => {
    const grouped: Record<string, User[]> = {
      admin: [],
      supervisor: [],
      monitor: [],
      worker: [],
      table: []
    };
    
    filteredUsers.forEach(user => {
      if (grouped[user.role]) {
        grouped[user.role].push(user);
      }
    });
    
    return grouped;
  }, [filteredUsers]);

  // Create new user
  const handleCreateUser = async () => {
    if (!newUser.name.trim()) {
      toast({
        title: 'Eroare',
        description: 'Numele este obligatoriu',
        variant: 'destructive'
      });
      return;
    }

    try {
      const password = generatePassword();
      const createdUser = await apiService.createUser({
        ...newUser,
        teamId: newUser.teamId ? parseInt(newUser.teamId) : undefined,
        assignedLocationId: newUser.assignedLocationId ? parseInt(newUser.assignedLocationId) : undefined,
        assignedProjectId: newUser.assignedProjectId ? parseInt(newUser.assignedProjectId) : undefined
      });

      onUpdateUser(createdUser.id, createdUser);
      
      toast({
        title: 'Succes',
        description: (
          <div>
            <p>Utilizator creat cu succes!</p>
            <p className="mt-2 font-mono bg-gray-100 p-2 rounded">
              Parolă: {password}
            </p>
          </div>
        ),
      });

      setShowCreateDialog(false);
      resetNewUserForm();
      onRefresh();
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut crea utilizatorul',
        variant: 'destructive'
      });
    }
  };

  // Reset password
  const handleResetPassword = async (userId: number, userName: string) => {
    try {
      console.log('Resetting password for user:', userId, userName);
      const response = await apiService.resetPassword(userId);
      console.log('Reset password response:', response);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to reset password');
      }
      
      // Extract the new password from the response message
      console.log('Response message:', response.message);
      const passwordMatch = response.message?.match(/New password: (.+)$/);
      const newPassword = passwordMatch ? passwordMatch[1] : '';
      console.log('Extracted password:', newPassword);
      
      if (!newPassword) {
        // If we couldn't extract the password, show the full message
        console.warn('Could not extract password from response');
        toast({
          title: 'Parolă resetată',
          description: response.message || 'Parola a fost resetată cu succes',
        });
      } else {
        // Show password in a dialog
        console.log('Setting password dialog data');
        setResetPasswordData({ userName, newPassword });
        setShowPasswordDialog(true);
        
        // Also show a success toast
        toast({
          title: 'Parolă resetată cu succes',
          description: `Parola pentru ${userName} a fost resetată.`,
        });
      }
      
      onRefresh(); // Refresh the user list
    } catch (error: unknown) {
      console.error('Error resetting password:', error);
      toast({
        title: 'Eroare',
        description: (error instanceof Error ? error.message : String(error)) || 'Nu s-a putut reseta parola',
        variant: 'destructive'
      });
    }
  };

  // Delete user (soft delete)
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await apiService.deleteUser(userToDelete.id);
      
      toast({
        title: 'Utilizator șters',
        description: `${userToDelete.name} a fost șters cu succes.`,
      });
      
      setShowDeleteDialog(false);
      setUserToDelete(null);
      onRemoveUser(userToDelete.id);
      onRefresh();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut șterge utilizatorul',
        variant: 'destructive'
      });
    }
  };

  const onDelete = (user: User) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const onEdit = (user: User) => {
    setSelectedUser(user);
    setShowEditDialog(true);
  };

  // Import users from Excel
  const handleImportUsers = async () => {
    if (!importFile) return;

    try {
      const data = await readExcelFile(importFile);
      const results = await apiService.bulkImportUsers(data);
      
      toast({
        title: 'Import finalizat',
        description: `${results.createdUsers.length} utilizatori creați, ${results.errors.length} erori`,
      });

      setShowImportDialog(false);
      setImportFile(null);
      onRefresh();
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut importa fișierul',
        variant: 'destructive'
      });
    }
  };

  // Export users to Excel
  const handleExportUsers = () => {
    try {
      console.log('Exporting users, count:', filteredUsers.length);
      
      if (filteredUsers.length === 0) {
        toast({
          title: 'Atenție',
          description: 'Nu există utilizatori de exportat',
          variant: 'destructive'
        });
        return;
      }
      
      const exportData = filteredUsers.map(user => ({
        ID: user.id,
        Nume: user.name,
        Rol: ROLE_NAMES[user.role],
        Email: user.email || '',
        Telefon: user.phone || '',
        Echipă: teams.find(t => t.id === user.teamId)?.name || '',
        'Tip Muncitor': user.workerType || ''
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Utilizatori');
      XLSX.writeFile(wb, `utilizatori-${new Date().toISOString().split('T')[0]}.xlsx`);
      
      toast({
        title: 'Export realizat',
        description: `${filteredUsers.length} utilizatori exportați cu succes`,
      });
    } catch (error) {
      console.error('Error exporting users:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut exporta lista de utilizatori',
        variant: 'destructive'
      });
    }
  };

  // Helper functions
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const resetNewUserForm = () => {
    setNewUser({
      name: '',
      role: 'worker',
      email: '',
      phone: '',
      teamId: '',
      workerType: 'Angajat',
      assignedLocationId: '',
      assignedProjectId: ''
    });
  };

  const readExcelFile = (file: File): Promise<unknown[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  // Role statistics
  const roleStats = useMemo(() => {
    return Object.entries(ROLE_NAMES).map(([role, name]) => ({
      role,
      name,
      count: allUsers.filter(u => u.role === role).length,
      icon: ROLE_ICONS[role as keyof typeof ROLE_ICONS],
      color: ROLE_COLORS[role as keyof typeof ROLE_COLORS]
    }));
  }, [allUsers]);

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Gestionare Utilizatori</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExportUsers}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export Excel
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowImportDialog(true)}
            className="flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            Import
          </Button>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Utilizator Nou
          </Button>
        </div>
      </div>

      {/* Role Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {roleStats.map(({ role, name, count, icon: Icon, color }) => (
          <Card key={role} className="hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setSelectedRole(role)}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{name}</p>
                  <p className="text-2xl font-bold">{count}</p>
                </div>
                <Icon className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Caută după nume, email sau ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Toate rolurile" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate rolurile</SelectItem>
                {Object.entries(ROLE_NAMES).map(([role, name]) => (
                  <SelectItem key={role} value={role}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={onRefresh}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Special Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PasswordRegeneration />
        <OrganizationalStructureExporter />
      </div>

      {/* Users List by Role */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="all">
            Toți ({filteredUsers.length})
          </TabsTrigger>
          {Object.entries(ROLE_NAMES).map(([role, name]) => (
            <TabsTrigger key={role} value={role}>
              {name} ({usersByRole[role]?.length || 0})
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <UsersList 
            users={filteredUsers} 
            teams={teams} 
            onEdit={(user) => {
              setSelectedUser(user);
              setShowEditDialog(true);
            }}
            onDelete={onDelete}
            onResetPassword={handleResetPassword}
            onWorkSettings={(userId, userName) => setWorkSettingsDialog({
              open: true,
              userId,
              userName
            })} 
          />
        </TabsContent>

        {Object.entries(ROLE_NAMES).map(([role]) => (
          <TabsContent key={role} value={role} className="mt-4">
            <UsersList 
              users={usersByRole[role] || []} 
              teams={teams} 
              onEdit={(user) => {
                setSelectedUser(user);
                setShowEditDialog(true);
              }}
              onDelete={onDelete}
              onResetPassword={handleResetPassword}
              onWorkSettings={(userId, userName) => setWorkSettingsDialog({
                open: true,
                userId,
                userName
              })}
            />
          </TabsContent>
        ))}
      </Tabs>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Creare Utilizator Nou</DialogTitle>
            <DialogDescription>
              Completați informațiile pentru noul utilizator
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nume complet</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Ex: Ion Popescu"
              />
            </div>

            <div>
              <Label>Rol</Label>
              <Select value={newUser.role} onValueChange={(value: User['role']) => setNewUser({ ...newUser, role: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_NAMES).map(([role, name]) => (
                    <SelectItem key={role} value={role}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(newUser.role === 'worker' || newUser.role === 'supervisor' || newUser.role === 'monitor') && (
              <div>
                <Label>Echipă</Label>
                <Select value={newUser.teamId} onValueChange={(value) => setNewUser({ ...newUser, teamId: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selectează echipa" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map(team => (
                      <SelectItem key={team.id} value={team.id.toString()}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {newUser.role === 'worker' && (
              <div>
                <Label>Tip Muncitor</Label>
                <Select value={newUser.workerType} onValueChange={(value) => setNewUser({ ...newUser, workerType: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Angajat">Angajat</SelectItem>
                    <SelectItem value="Zilier">Zilier</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {newUser.role === 'table' && (
              <>
                <div>
                  <Label>Locație Asignată</Label>
                  <Select value={newUser.assignedLocationId} onValueChange={(value) => setNewUser({ ...newUser, assignedLocationId: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selectează locația" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map(location => (
                        <SelectItem key={location.id} value={location.id.toString()}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Proiect Asignat</Label>
                  <Select value={newUser.assignedProjectId} onValueChange={(value) => setNewUser({ ...newUser, assignedProjectId: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selectează proiectul" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(project => (
                        <SelectItem key={project.id} value={project.id.toString()}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div>
              <Label>Email (opțional)</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>

            <div>
              <Label>Telefon (opțional)</Label>
              <Input
                value={newUser.phone}
                onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                placeholder="07XXXXXXXX"
              />
            </div>

            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                {newUser.role === 'table' 
                  ? 'Parola pentru contul de masă va fi: table123'
                  : 'O parolă aleatorie va fi generată automat și afișată după creare.'}
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              resetNewUserForm();
            }}>
              Anulează
            </Button>
            <Button onClick={handleCreateUser}>
              Creează Utilizator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editare Utilizator</DialogTitle>
            <DialogDescription>
              Modificați informațiile utilizatorului
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nume complet</Label>
              <Input
                value={selectedUser?.name || ''}
                onChange={(e) => setSelectedUser(selectedUser ? { ...selectedUser, name: e.target.value } : null)}
                placeholder="Ex: Ion Popescu"
              />
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={selectedUser?.email || ''}
                onChange={(e) => setSelectedUser(selectedUser ? { ...selectedUser, email: e.target.value } : null)}
                placeholder="email@example.com"
              />
            </div>

            {(selectedUser?.role === 'worker' || selectedUser?.role === 'supervisor' || selectedUser?.role === 'monitor') && (
              <div>
                <Label>Echipă</Label>
                <Select 
                  value={selectedUser?.teamId?.toString() || ''} 
                  onValueChange={(value) => setSelectedUser(selectedUser ? { ...selectedUser, teamId: parseInt(value) } : null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selectează echipa" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map(team => (
                      <SelectItem key={team.id} value={team.id.toString()}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditDialog(false);
              setSelectedUser(null);
            }}>
              Anulează
            </Button>
            <Button onClick={async () => {
              if (selectedUser) {
                try {
                  const updatedUser = await apiService.updateUser(selectedUser.id, {
                    name: selectedUser.name,
                    email: selectedUser.email,
                    teamId: selectedUser.teamId
                  });
                  onUpdateUser(selectedUser.id, updatedUser);
                  toast({
                    title: 'Succes',
                    description: 'Utilizatorul a fost actualizat cu succes',
                  });
                  setShowEditDialog(false);
                  setSelectedUser(null);
                  onRefresh();
                } catch (error) {
                  toast({
                    title: 'Eroare',
                    description: 'Nu s-a putut actualiza utilizatorul',
                    variant: 'destructive'
                  });
                }
              }
            }}>
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Utilizatori din Excel</DialogTitle>
            <DialogDescription>
              Selectați un fișier Excel cu utilizatorii de importat
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Fișier Excel</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              />
            </div>

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <p className="font-semibold mb-2">Format necesar:</p>
                <ul className="text-sm space-y-1">
                  <li>• Nume (obligatoriu)</li>
                  <li>• Rol (admin/supervisor/monitor/worker/table)</li>
                  <li>• Email (opțional)</li>
                  <li>• Telefon (opțional)</li>
                  <li>• Echipă (numele echipei pentru workers)</li>
                  <li>• Tip Muncitor (Angajat/Zilier pentru workers)</li>
                </ul>
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowImportDialog(false);
              setImportFile(null);
            }}>
              Anulează
            </Button>
            <Button onClick={handleImportUsers} disabled={!importFile}>
              <Upload className="h-4 w-4 mr-2" />
              Importă
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-blue-600" />
              Parolă Nouă Generată
            </DialogTitle>
            <DialogDescription>
              Parola a fost resetată cu succes pentru utilizatorul {resetPasswordData?.userName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert className="border-blue-200 bg-blue-50">
              <CheckCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                Vă rugăm să comunicați această parolă utilizatorului într-un mod sigur.
              </AlertDescription>
            </Alert>
            
            <div className="bg-gray-100 p-4 rounded-lg">
              <Label className="text-sm text-gray-600 mb-2 block">Parolă nouă:</Label>
              <div className="flex items-center justify-between gap-2">
                <code className="text-lg font-mono font-bold text-gray-900 select-all">
                  {resetPasswordData?.newPassword}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(resetPasswordData?.newPassword || '');
                    toast({
                      title: 'Copiat',
                      description: 'Parola a fost copiată în clipboard',
                    });
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Utilizatorul va trebui să își schimbe parola la prima autentificare.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button onClick={() => {
              setShowPasswordDialog(false);
              setResetPasswordData(null);
            }}>
              Închide
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirmare Ștergere Utilizator
            </DialogTitle>
            <DialogDescription>
              Sunteți sigur că doriți să ștergeți utilizatorul?
            </DialogDescription>
          </DialogHeader>
          
          {userToDelete && (
            <div className="space-y-4">
              <Alert className="border-yellow-200 bg-yellow-50">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  Această acțiune va șterge utilizatorul din sistem. Toate datele istorice vor fi păstrate pentru rapoarte.
                </AlertDescription>
              </Alert>
              
              <div className="bg-gray-100 p-4 rounded-lg space-y-2">
                <div>
                  <span className="font-semibold">Nume:</span> {userToDelete.name}
                </div>
                <div>
                  <span className="font-semibold">Rol:</span> {ROLE_NAMES[userToDelete.role]}
                </div>
                {userToDelete.email && (
                  <div>
                    <span className="font-semibold">Email:</span> {userToDelete.email}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setUserToDelete(null);
              }}
            >
              Anulare
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Șterge Utilizator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Work Settings Dialog */}
      <WorkSettingsDialog
        open={workSettingsDialog.open}
        onOpenChange={(open) => setWorkSettingsDialog(prev => ({ ...prev, open }))}
        userId={workSettingsDialog.userId}
        userName={workSettingsDialog.userName}
        onSettingsUpdated={onRefresh}
      />
    </div>
  );
};

// Users List Component
const UsersList: React.FC<{
  users: User[];
  teams: Team[];
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onResetPassword: (userId: number, userName: string) => void;
  onWorkSettings: (userId: number, userName: string) => void;
}> = ({ users, teams, onEdit, onDelete, onResetPassword, onWorkSettings }) => {
  if (users.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Nu există utilizatori în această categorie</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {users.map((user) => {
        const Icon = ROLE_ICONS[user.role];
        const team = teams.find(t => t.id === user.teamId);
        
        return (
          <Card key={user.id} className="hover:shadow-md transition-shadow overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <Icon className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">{user.name}</h4>
                    <Badge className={ROLE_COLORS[user.role]} variant="secondary">
                      {ROLE_NAMES[user.role]}
                    </Badge>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Badge variant="outline">ID: {user.id}</Badge>
                  {user.workerType && (
                    <Badge variant="outline">{user.workerType}</Badge>
                  )}
                </div>

                {user.email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="h-3 w-3" />
                    <span className="truncate">{user.email}</span>
                  </div>
                )}

                {user.phone && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="h-3 w-3" />
                    <span>{user.phone}</span>
                  </div>
                )}

                {team && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Building2 className="h-3 w-3" />
                    <span>{team.name}</span>
                  </div>
                )}
              </div>

              <div className="space-y-3 mt-6 border-t pt-4">
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="default"
                    onClick={() => onWorkSettings(user.id, user.name)}
                    className="w-full justify-start"
                    title="Setează timpul de pauză și norma zilnică"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    <span>Setări Pauză</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="default"
                    onClick={() => onResetPassword(user.id, user.name)}
                    className="w-full justify-start"
                    title="Resetează parola"
                  >
                    <Key className="h-4 w-4 mr-2" />
                    <span>Resetare Parolă</span>
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="default"
                    onClick={() => onEdit(user)}
                    className="w-full justify-start"
                    title="Editează utilizatorul"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    <span>Editează</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="default"
                    onClick={() => onDelete(user)}
                    className="w-full justify-start hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                    title="Șterge utilizatorul"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    <span>Șterge</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};